<template>
    <component :is="tag">
        <slot
            :is-loading="isLoading"
            :is-delivering="isDelivering"
            :deliver-task="deliverTask"
            :errors="errors"
            :error-message="errorMessage"
            :go-to-exit-route="goToExitRoute"
            :go-back="goBack"
            :task="task"
            :brand-networks="brandNetworks"
            :upload-content-url="uploadContentUrl"
            :delete-uploaded-content-url="deleteUploadedContentUrl"
            :uploaded-content-delivery-url="uploadedContentDeliveryUrl"
            :handle-file-change="handleFileChange"
            :handle-reset-uploads="handleResetUploads"
            :handle-upload-failed="handleUploadFailed"
            :upload-headers="uploadHeaders"
            :uploaded-files="uploadedFiles"
            :has-uploaded-files="hasUploadedFiles"
            :is-file-uploading="isFileUploading"
            :task-content="taskContent"
            :is-task-content-loading="isTaskContentLoading"
            :quantity-string="quantityString(task)"
        />
    </component>
</template>

<script>
    import { message } from '@grininc/grin-ui';
    import ActivationBriefTasksMixin from '../mixins/ActivationBriefTasksMixin';
    import ActivationTasksMixin from '../../../components/data-views/activation-briefs/mixins/ActivationTasksMixin';
    import BaseActivationBriefStepMixin from '../mixins/BaseActivationBriefStepMixin';
    import ContactsMixin from '../mixins/ContactsMixin';
    import UploadMixin from '../mixins/UploadMixin';
    import useBrands from '../../../../src/composables/use-brands';
    import useActivationBriefs from '../../../../src/composables/use-activation-briefs';
    import useFeatureFlagsStore from '../../../../stores/feature-flags';

    export default {
        name: 'ActivationBriefTaskDeliveryProvider',
        mixins: [
            ContactsMixin,
            BaseActivationBriefStepMixin,
            ActivationBriefTasksMixin,
            UploadMixin,
            ActivationTasksMixin
        ],
        data() {
            const exitRoute =
                this.$route?.params?.context === 'summary' ? 'v2.activation.tasks.summary' : 'v2.activation.brief.tasks';
            const backRoute = this.$route?.params?.back_route || 'v2.activation.brief.tasks';
            const context = this.$route?.params?.context;
            return {
                isDelivering: false,
                isFileUploading: false,
                isFileCanceling: false,
                brandNetworks: null,
                exitRoute,
                context,
                backRoute
            };
        },
        computed: {
            deliveryUrl() {
                if (this.taskRequiresApproval && this.deliverableParamId) {
                    return `/api/v1/activation-briefs/${this.briefParamId}/deliverables/${this.deliverableParamId}/content`;
                }
                return `/api/v1/activation-briefs/${this.briefParamId}/tasks/${this.taskParamId}/content`;
            },
            uploadedContentDeliveryUrl() {
                if (this.taskRequiresApproval && this.deliverableParamId) {
                    return `/api/v1/activation-briefs/${this.briefParamId}/deliverables/${this.deliverableParamId}/uploaded-content`;
                }
                return `/api/v1/activation-briefs/${this.briefParamId}/tasks/${this.taskParamId}/uploaded-content`;
            },
            uploadContentUrl() {
                return `/activation-briefs/${this.briefParamId}/tasks/${this.taskParamId}/uploaded-content/upload?network_id=${this.task?.network_id}`;
            },
            deleteUploadedContentUrl() {
                return `/activation-briefs/${this.briefParamId}/tasks/${this.taskParamId}/uploaded-content/`;
            },
            taskDeliveryImprovementEnabled() {
                const featureFlags = useFeatureFlagsStore().data;
                return featureFlags['task-delivery-improvements-enabled'];
            }
        },
        methods: {
            gotoTaskStep() {
                this.$router.push({ name: this.exitRoute });
            },
            goToExitRoute() {
                this.$router.push({
                    name: this.exitRoute,
                    params: { context: this.context }
                });
            },
            goBack() {
                this.$router.push({
                    name: this.backRoute,
                    params: { context: this.context }
                });
            },
            async deliverTask(url, postedAt) {
                this.isDelivering = true;
                this.resetErrors();

                const { getActivationBriefs } = useActivationBriefs();
                try {
                    await this.$http.post(this.deliveryUrl, {
                        url,
                        posted_at: postedAt
                    });

                    if (this.taskDeliveryImprovementEnabled) {
                        const res = await getActivationBriefs(this.contactId);
                        this.$root.briefs = res.data;
                    }

                    if (!this.isTaskEvergreen) {
                        this.gotoTaskStep();
                    }

                    message.success('Nice! Your task has been delivered');
                } catch (error) {
                    let message = 'Something went wrong. Please try again.';

                    if (error.status === 423 || error.status === 409) {
                        message = 'Content has already been delivered.';
                    } else if (error.status === 404) {
                        message = 'Task could not be found.';
                    }
                    this.errorMessage = message;
                    this.handleHttpError(error);
                } finally {
                    this.isDelivering = false;
                }
            },
            handleFileChange(info) {
                if (this.isFileCanceling) {
                    this.isFileCanceling = false;
                    return;
                }

                this.isFileUploading = true;
                this.handleUploadChange(
                    info,
                    () => (this.isFileUploading = false),
                    () => {
                        this.handleUploadError(info);
                        this.isFileUploading = false;
                    },
                    true
                );
            },
            handleResetUploads() {
                this.isFileUploading = false;
                this.handleResetUploadedFiles();
            },
            handleUploadFailed() {
                this.isFileUploading = false;
                this.isFileCanceling = true;
            },
            async loadBrandNetworks() {
                const response = await useBrands().getSocialAccounts(this.brief?.brand?.id);
                this.brandNetworks = response.data;
            }
        },

        async created() {
            this.isLoading = true;

            try {
                await this.loadContact(true);
                await this.loadActivationBrief(true);
                await this.loadBrandNetworks();
                this.loadActivationTaskContent();
            } catch (error) {
                this.handleHttpError(error);
            }

            this.isLoading = false;
        }
    };
</script>
